import { FC, useCallback, useMemo } from 'react';
import { EntityDeletionState, useRepositoryQuery } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useEnterpriseNavigate, useTimeService } from '@mews-commander/ui';
import { localizedEntities } from '@mews-commander/legacy-entities/src/ducks/utils';
import { useCommanderSelector } from '@mews-commander/utils';
import { checkEnterprisePrivilege } from '@mews-commander/legacy-entities';
import { useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { LocalizedProduct } from '../types';
import { RoutingRuleDetail } from '../RoutingRuleDetail/RoutingRuleDetail';
import { getSelectedServiceProductsOfferedToEmployee } from '../../../newReservation/selectors';
import { useServicesSuspenseQuery } from '../form/steps/itemsAndConditions/queries';
import { mapRoutingRuleToRoutingRuleDetail } from './utils/mapRoutingRuleToRoutingRuleDetail';

interface RoutingRuleDetailContainerProps {
    routingRuleId: string | null;
    serviceIds?: string[];
    productIds?: string[];
    opened: boolean;
    onClose: () => void;
    aggregateItems?: boolean;
    scope?: string;
}

export const RoutingRuleDetailContainer: FC<RoutingRuleDetailContainerProps> = ({
    routingRuleId,
    onClose,
    opened,
    serviceIds,
    productIds,
    aggregateItems,
    scope,
}) => {
    const enterpriseId = useCurrentEnterpriseId();
    const timeService = useTimeService();
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );

    const { data: routingRuleResult, isFetching: isFetchingRoutingRule } = useRepositoryQuery(
        'routingRules',
        'newGetAll',
        {
            routingRuleIds: routingRuleId ? [routingRuleId] : [],
            routingRuleDeletionState: EntityDeletionState.Everything,
            extent: {
                productAssignments: true,
                rateAssignments: true,
                productCategoryAssignments: true,
                accountingCategoryAssignments: true,
                routingRuleAssignments: true,
                profiles: true,
            },
        },
        {
            enabled: opened && !!routingRuleId,
        }
    );

    const { data: productCategoriesResult, isFetching: isFetchingProductCategories } = useRepositoryQuery(
        'productCategories',
        'getAll',
        {
            enterpriseIds: [enterpriseId],
        }
    );

    const { data: allServices } = useServicesSuspenseQuery();

    const allProducts = useCommanderSelector((state) =>
        localizedEntities(
            getSelectedServiceProductsOfferedToEmployee,
            'names',
            'localizedName'
        )(state, { scope: scope || '' })
    ) as unknown as LocalizedProduct[];

    const routingRuleDetailData = useMemo(
        () =>
            mapRoutingRuleToRoutingRuleDetail({
                routingRuleResult,
                additionalData: {
                    allProducts,
                    allProductCategories: productCategoriesResult?.productCategories || undefined,
                    allServices,
                },
                timeService,
                filters: {
                    serviceIds,
                    productIds,
                },
                aggregateItems,
                granularRoutingRulesConditionsExtensionEnabled,
            }),
        [
            routingRuleResult,
            productCategoriesResult,
            serviceIds,
            productIds,
            allProducts,
            aggregateItems,
            timeService,
            allServices,
            granularRoutingRulesConditionsExtensionEnabled,
        ]
    );

    const hasEnterprisePrivilege = useCommanderSelector((state) => checkEnterprisePrivilege(state, { enterpriseId }));
    const navigate = useEnterpriseNavigate();
    const onEdit = useCallback(() => {
        if (!routingRuleId) return;
        void navigate({
            to: 'RoutingRule/Detail/$routingRuleId',
            params: { routingRuleId },
        });
    }, [routingRuleId, navigate]);

    return (
        <RoutingRuleDetail
            onClose={onClose}
            loading={isFetchingRoutingRule || isFetchingProductCategories}
            onEdit={hasEnterprisePrivilege ? onEdit : undefined}
            opened={opened}
            {...routingRuleDetailData}
        />
    );
};
